'use client';

import { type ElementType, forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { ButtonGroupContext } from '../button-group/context/button-group.context';
import { Slot } from '../slot';
import { IconButtonRootContext } from './context/icon-button-root.context';
import { iconButtonRecipe } from './styles/icon-button-root.styles';
import type { IconButtonRootPropsInterface } from './types/icon-button-root.types';

/**
 * @since 10.13.0
 */
export const IconButtonRoot = forwardRef<HTMLButtonElement, IconButtonRootPropsInterface>(
  (props: IconButtonRootPropsInterface, forwardedRef) => {
    const context = useContext(ButtonGroupContext);

    const {
      asChild = false,
      emphasis = 'outlined',
      tone = 'neutral',
      children,
      className,
      css: cssProp,
      disabled = false,
      loading = false,
      onImpact = false,
      shape = 'square',
      size = 'md',
      tabIndex,
      type = 'button',
      ...rest
    } = props;

    const resolvedTone = context.tone || tone;
    const resolvedEmphasis = context.emphasis || emphasis;
    const resolvedSize = context.size || size;
    const isDisabled = disabled || loading;

    const styles = css(
      iconButtonRecipe.raw({
        disabled: isDisabled,
        loading,
        onImpact,
        shape,
        size: resolvedSize,
        tone: resolvedTone,
        emphasis: resolvedEmphasis,
      }),
      cssProp,
    );

    // Loading IconButtons stay focusable so users don't lose their place.
    const loadingDisabledInteractionsProps = useDisabledInteractions({ focusable: true });

    // Real `<button>` uses the native `disabled` attribute; when rendered as an
    // anchor via `asChild`, fall back to aria-disabled + tabIndex + event blocking.
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    const rootContextValue = useMemo(
      () => ({
        tone: resolvedTone,
        emphasis: resolvedEmphasis,
        onImpact,
        size: resolvedSize,
      }),
      [resolvedTone, resolvedEmphasis, onImpact, resolvedSize],
    );

    const Component: ElementType = asChild ? Slot : 'button';

    return (
      <IconButtonRootContext.Provider value={rootContextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="IconButton"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_icon-button', styles, className)}
          disabled={!asChild && disabled ? true : undefined}
          type={asChild ? undefined : type}
          {...rest}
          {...(asChild && disabled
            ? disabledInteractionsProps
            : loading
              ? loadingDisabledInteractionsProps
              : { tabIndex })}
        >
          {children}
        </Component>
      </IconButtonRootContext.Provider>
    );
  },
);

IconButtonRoot.displayName = 'IconButton.Root';
